import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../api";
import { toasts } from "../components/AppToast";

import Layout from "../components/Layout";
import { FiTrash2, FiEdit, FiPlus, FiSearch } from "react-icons/fi";
import Pagination from "../components/Pagination";

export default function CarItems() {
  const navigate = useNavigate();

  // Table
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Form
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete modal
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // Auth
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  // Fetch data
  const fetchData = useCallback(async (pg, q) => {
    setLoading(true);

    try {
      const res = await API.get("/caritems", {
        params: {
          page: pg,
          limit: 10,
          search: q,
        },
      });

      setData(res.data?.data || []);
      setTotalPages(Number(res.data?.totalPages || 1));
      setTotal(Number(res.data?.total || 0));
    } catch (error) {
      console.error("Fetch Car Items error:", error);

      toasts.error(
        error.response?.data?.error || "Failed to load Car Items"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Load table when page changes
  useEffect(() => {
    fetchData(page, search);
  }, [page, fetchData, search]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(1, search);
    }, 350);

    return () => clearTimeout(timer);
  }, [search, fetchData]);

  // Add / Update
  const handleSubmit = async () => {
    const userData = JSON.parse(localStorage.getItem("user") || "null");

    if (!name.trim()) {
      toasts.warning("Enter Car Item name");
      return;
    }

    if (!userData?.user_id) {
      toasts.warning("User information not available");
      localStorage.clear();
      navigate("/login");
      return;
    }

    setSaving(true);

    try {
      if (editId) {
        await API.put(`/caritems/${editId}`, {
          name: name.trim(),
          user_id: userData.user_id,
        });

        toasts.success("Car Item updated successfully");
      } else {
        await API.post("/caritems", {
          name: name.trim(),
          user_id: userData.user_id,
        });

        toasts.success("Car Item added successfully");
      }

      setName("");
      setEditId(null);

      await fetchData(page, search);
    } catch (error) {
      console.error("Submit error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // Edit
  const handleEdit = (row) => {
    setName(row.item_name);
    setEditId(row.item_id);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Cancel edit
  const handleCancel = () => {
    setName("");
    setEditId(null);
  };

  // Open delete modal
  const handleDelete = (row) => {
    setDelModal(row);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    if (delSaving) {
      return;
    }

    setDelModal(null);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!delModal?.item_id) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(`/caritems/${delModal.item_id}`);

      toasts.success("Car Item deleted successfully");

      setDelModal(null);

      const remainingRecords = Math.max(0, total - 1);
      const newTotalPages = Math.max(
        1,
        Math.ceil(remainingRecords / 10)
      );

      if (page > 1 && page > newTotalPages) {
        setPage((p) => Math.max(1, p - 1));
      } else {
        await fetchData(page, search);
      }

      if (editId === delModal.item_id) {
        setName("");
        setEditId(null);
      }
    } catch (error) {
      console.error("Delete error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };
  // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };
  return (
    <Layout activePage="Car Items">
      <main className="dashboard-main">
        {/* Main card */}
        <div className="season-card">
          {/* Top controls */}
          <div className="season-top-row">
            {/* Form */}
            <div className="season-form-row">
              {editId && (
                <span className="season-editing-badge">
                  Editing #{editId}
                </span>
              )}

              <input
                type="text"
                placeholder="Enter Car Items"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !saving) {
                    handleSubmit();
                  }
                }}
                className="season-input"
              />

              {editId && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="season-cancel-btn"
                  disabled={saving}
                >
                  Cancel
                </button>
              )}

              <button
                type="button"
                onClick={handleSubmit}
                className="um-btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <span className="spinner" />
                ) : editId ? (
                  <>
                    <FiEdit />
                    Update Car Item
                  </>
                ) : (
                  <>
                    <FiPlus />
                    Add Car Item
                  </>
                )}
              </button>
            </div>

            {/* Search */}
            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch size={17} />
              </span>

              <input
                type="text"
                placeholder="Search Car Items..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-striped table-hover table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr className="um-table-head">
                  <th className="um-th text-nowrap">#</th>
                  <th className="um-th text-nowrap text-center">
                    Car Items
                  </th>
                  <th className="um-th text-nowrap text-center">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} className="um-td text-center">
                      Loading…
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="empty-table-cell text-center py-5"
                    >
                      No Car Items found
                    </td>
                  </tr>
                ) : (
                  data.map((row, i) => (
                    <tr
                      key={row.item_id}
                      className={`um-row ${
                        editId === row.item_id ? "editing-row" : ""
                      }`}
                    >
                      {/* Number */}
                      <td className="um-td user-number text-center">
                        {(page - 1) * 10 + i + 1}
                      </td>

                      {/* Item name */}
                      <td className="um-td text-center">
                        <span className="season-name-chip">
                          {row.item_name}
                        </span>
                      </td>

                      {/* Action */}
                      <td className="um-td text-center season-action-td">
                        <div className="action-buttons d-flex justify-content-center align-items-center gap-2">
                          {/* Edit */}
                          <button
                            type="button"
                            onClick={() => handleEdit(row)}
                            className="action-btn action-edit btn btn-sm"
                            title="Edit"
                          >
                            <FiEdit size={15} />
                          </button>

                          {/* Delete */}
                          <button
                            type="button"
                            onClick={() => handleDelete(row)}
                            className="action-btn action-delete btn btn-sm"
                            title="Delete"
                            disabled={delSaving}
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={10}
              loading={loading}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          </div>
        </div>
      </main>

      {/* Delete modal */}
      {delModal && (
        <div className="um-overlay" onClick={closeDeleteModal}>
          <div
            className="um-modal um-delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-content">
              {/* Icon */}
              <div className="delete-icon">
                <FiTrash2 size={32} />
              </div>

              {/* Title */}
              <h2 className="delete-title">
                Delete Car Item?
              </h2>

              {/* Description */}
              <p className="delete-description">
                Are you sure you want to delete{" "}
                <span className="badge badge-success">
                  "{delModal.item_name}"
                </span>
                ?
                <br />
                This will permanently remove this car item.
                <br />
                <strong>This action cannot be undone.</strong>
              </p>

              {/* Actions */}
              <div className="delete-actions">
                {/* Cancel */}
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeDeleteModal}
                  disabled={delSaving}
                >
                  Cancel
                </button>

                {/* Delete */}
                <button
                  type="button"
                  disabled={delSaving}
                  className="delete-confirm-btn"
                  onClick={confirmDelete}
                >
                  {delSaving ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <FiTrash2 />
                      Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

     
    </Layout>
  );
}
